// Setup › Finance › Recurring costs (`/c/{slug}/setup/finance/expenses`; 11 §Seats carry their
// cost, M5-03 `recurringExpenses.*`): benefits (on a seat; restricted like pay), overhead and
// software subscriptions on a seat, a team or the company, monthly, quarterly or yearly from a
// month. Like pay they are accrued by the finance views, never posted to the ledger. Managers
// read overhead and software; benefits are the owner's, finance admins' and a seat's pay viewer's;
// finance admins write. Calls on load: the standing, then the rows; the org chart for names.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarXIcon, PencilIcon, PlusIcon, RepeatIcon } from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { toMinor } from "../costs/costs-page";
import { formatMoney, fractionDigits } from "../format";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";
import {
  type Currency,
  CurrencySelect,
  FinancePage,
  FormDialog,
  FormField,
  MonthSelect,
  monthKeyOf,
  monthLabel,
  monthsAround,
  ReadOnlyNotice,
  useFinanceStanding,
  useOrgNames,
} from "./finance-parts";
import { financeKeys, recurringQuery } from "./finance-queries";

type Expense = Awaited<ReturnType<Api["client"]["recurringExpenses"]["list"]>>["data"][number];
type Kind = Expense["kind"];
type Cadence = Expense["cadence"];

export const EXPENSE_KIND_LABELS: Record<Kind, string> = {
  benefit: "Benefit",
  overhead: "Overhead",
  software_subscription: "Software",
};
const CADENCE_LABELS: Record<Cadence, string> = {
  monthly: "a month",
  quarterly: "a quarter",
  yearly: "a year",
};

const major = (minor: number, currency: string) =>
  (minor / 10 ** fractionDigits(currency)).toFixed(fractionDigits(currency));

export function ExpensesScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const standing = useFinanceStanding(api, company);
  const list = useInfiniteQuery({ ...recurringQuery(api, company.id), enabled: standing.loaded });
  const names = useOrgNames(api, company);
  const [editing, setEditing] = useState<Expense | "new" | null>(null);
  const qc = useQueryClient();
  const current = monthKeyOf(new Date());
  const end = useMutation({
    mutationFn: (row: Expense) =>
      api.client.recurringExpenses.update({
        params: { co: company.id, id: row.id },
        body: { end_month: current < row.start_month ? row.start_month : current },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.all(company.id) }),
  });
  const rows = list.data?.pages.flatMap((p) => p.data) ?? [];
  const where = (e: Expense) =>
    e.seat_id ? names.seatName(e.seat_id) : e.team_id ? names.teamName(e.team_id) : "Company";

  return (
    <FinancePage
      company={company}
      where="setup"
      title="Recurring costs"
      icon={RepeatIcon}
      actions={
        standing.admin ? (
          <Button size="sm" onClick={() => setEditing("new")}>
            <PlusIcon aria-hidden="true" />
            Add a cost
          </Button>
        ) : null
      }
    >
      {standing.query.isPending || list.isPending ? (
        <Loading label="Loading recurring costs" rows={3} />
      ) : standing.query.isError ? (
        <LoadError what="Your finance access" error={standing.query.error} />
      ) : list.isError ? (
        <LoadError what="Recurring costs" error={list.error} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {!standing.admin ? <ReadOnlyNotice company={company} owner={standing.owner} /> : null}
          {rows.length === 0 ? (
            <EmptyState
              variant="card"
              icon={RepeatIcon}
              title="No recurring costs"
              description="Add rent, tools or benefits so budget vs actual and margins carry them."
            />
          ) : (
            <>
              <Table data-slot="recurring-expenses">
                <TableCaption>
                  {standing.restricted
                    ? "Benefits are shown to the owner, finance admins and the seat's pay viewer only."
                    : "Benefits you may not read are left out."}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Cost</TableHead>
                    <TableHead scope="col">Kind</TableHead>
                    <TableHead scope="col" className="max-md:hidden">
                      On
                    </TableHead>
                    <TableHead scope="col" className="text-right">
                      Amount
                    </TableHead>
                    <TableHead scope="col" className="max-sm:hidden">
                      Months
                    </TableHead>
                    <TableHead scope="col">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((e) => (
                    <TableRow key={e.id} className="group/row" data-expense={e.id}>
                      <TableHead scope="row" className="font-normal">
                        {e.name}
                      </TableHead>
                      <TableCell>
                        <Badge variant={e.kind === "benefit" ? "info" : "neutral"}>
                          {EXPENSE_KIND_LABELS[e.kind]}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-md:hidden">{where(e)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(e.amount_minor, e.currency)} {CADENCE_LABELS[e.cadence]}
                      </TableCell>
                      <TableCell className="text-muted-foreground max-sm:hidden">
                        {monthLabel(e.start_month)}
                        {e.end_month ? ` – ${monthLabel(e.end_month)}` : " onwards"}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {standing.admin ? (
                          <RowActions>
                            <RowAction
                              icon={PencilIcon}
                              label={`Edit ${e.name}`}
                              tip="Edit"
                              onClick={() => setEditing(e)}
                            />
                            {e.end_month === null ? (
                              <ConfirmRowAction
                                icon={CalendarXIcon}
                                label={`End ${e.name} this month`}
                                tip="End this month"
                                disabled={end.isPending}
                                title={`End “${e.name}” this month?`}
                                description="This month is its last; it stays on record for the months it ran."
                                confirmLabel="End this month"
                                onConfirm={() => end.mutate(e)}
                              />
                            ) : null}
                          </RowActions>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                what="costs"
                hasMore={list.hasNextPage}
                loading={list.isFetchingNextPage}
                onMore={() => void list.fetchNextPage()}
              />
            </>
          )}
        </div>
      )}
      {editing ? (
        <ExpenseDialog
          api={api}
          company={company}
          row={editing === "new" ? null : editing}
          seats={names.seats}
          teams={names.teams}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </FinancePage>
  );
}

function ExpenseDialog({
  api,
  company,
  row,
  seats,
  teams,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  row: Expense | null;
  seats: { id: string; title: string }[];
  teams: { id: string; name: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const current = monthKeyOf(new Date());
  const months = useMemo(() => monthsAround(current, 24, 24), [current]);
  const [name, setName] = useState(row?.name ?? "");
  const [kind, setKind] = useState<Kind>(row?.kind ?? "overhead");
  const [on, setOn] = useState(
    row?.seat_id ? `seat:${row.seat_id}` : row?.team_id ? `team:${row.team_id}` : "",
  );
  const [amount, setAmount] = useState(row ? major(row.amount_minor, row.currency) : "");
  const [currency, setCurrency] = useState<Currency>((row?.currency as Currency) ?? "USD");
  const [cadence, setCadence] = useState<Cadence>(row?.cadence ?? "monthly");
  const [start, setStart] = useState(row?.start_month ?? current);
  const [end, setEnd] = useState(row?.end_month ?? "");
  const ids = {
    name: useId(),
    kind: useId(),
    on: useId(),
    amount: useId(),
    cur: useId(),
    cadence: useId(),
    start: useId(),
    end: useId(),
  };
  const minor = toMinor(amount, currency);
  const benefitWithoutSeat = kind === "benefit" && !on.startsWith("seat:");
  const shared = {
    name: name.trim(),
    amount_minor: minor ?? 0,
    currency,
    cadence,
    seat_id: on.startsWith("seat:") ? on.slice(5) : null,
    team_id: on.startsWith("team:") ? on.slice(5) : null,
    start_month: start,
    end_month: end || null,
  };
  const save = useMutation({
    mutationFn: () =>
      row
        ? api.client.recurringExpenses.update({
            params: { co: company.id, id: row.id },
            body: shared,
          })
        : api.client.recurringExpenses.create({
            params: { co: company.id },
            idempotencyKey: newIdempotencyKey(),
            body: { ...shared, kind },
          }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={row ? "Edit recurring cost" : "Add a recurring cost"}
      submitLabel={row ? "Save" : "Add cost"}
      pending={save.isPending}
      error={save.error}
      canSubmit={
        name.trim().length > 0 &&
        minor !== null &&
        minor >= 0 &&
        !benefitWithoutSeat &&
        (!end || end >= start)
      }
      onSubmit={() => save.mutate()}
    >
      <FormField id={ids.name} label="Name">
        <Input
          id={ids.name}
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        {row ? null : (
          <FormField id={ids.kind} label="Kind">
            <NativeSelect
              id={ids.kind}
              value={kind}
              onChange={(e) => setKind(e.target.value as Kind)}
            >
              {(Object.keys(EXPENSE_KIND_LABELS) as Kind[]).map((k) => (
                <NativeSelectOption key={k} value={k}>
                  {EXPENSE_KIND_LABELS[k]}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          </FormField>
        )}
        <FormField
          id={ids.on}
          label="On"
          error={benefitWithoutSeat ? "A benefit is on a seat." : null}
        >
          <NativeSelect id={ids.on} value={on} onChange={(e) => setOn(e.target.value)}>
            <NativeSelectOption value="">The company</NativeSelectOption>
            {teams.map((t) => (
              <NativeSelectOption key={t.id} value={`team:${t.id}`}>
                Team: {t.name}
              </NativeSelectOption>
            ))}
            {seats.map((s) => (
              <NativeSelectOption key={s.id} value={`seat:${s.id}`}>
                Seat: {s.title}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
        <FormField
          id={ids.amount}
          label="Amount"
          error={
            amount !== "" && (minor === null || minor < 0) ? "Enter an amount like 120.00." : null
          }
        >
          <Input
            id={ids.amount}
            inputMode="decimal"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </FormField>
        <FormField id={ids.cur} label="Currency">
          <CurrencySelect id={ids.cur} value={currency} onChange={setCurrency} />
        </FormField>
        <FormField id={ids.cadence} label="Every">
          <NativeSelect
            id={ids.cadence}
            value={cadence}
            onChange={(e) => setCadence(e.target.value as Cadence)}
          >
            <NativeSelectOption value="monthly">Month</NativeSelectOption>
            <NativeSelectOption value="quarterly">
              Quarter (from its first month)
            </NativeSelectOption>
            <NativeSelectOption value="yearly">Year (from its first month)</NativeSelectOption>
          </NativeSelect>
        </FormField>
        <FormField id={ids.start} label="From">
          <MonthSelect id={ids.start} value={start} onChange={setStart} months={months} />
        </FormField>
        <FormField
          id={ids.end}
          label="Until"
          error={end && end < start ? "Ends before it starts." : null}
        >
          <NativeSelect id={ids.end} value={end} onChange={(e) => setEnd(e.target.value)}>
            <NativeSelectOption value="">No end</NativeSelectOption>
            {months.map((m) => (
              <NativeSelectOption key={m} value={m}>
                {monthLabel(m)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
      </div>
    </FormDialog>
  );
}
